import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

export interface CompressionOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  useWebWorker?: boolean;
  maxRetries?: number;
  quality?: number;
}

@Injectable({
  providedIn: 'root',
})
export class VideoCompressionService {
  constructor() {}

  compressVideo(
    file: File,
    options: CompressionOptions = {},
  ): Observable<File> {
    const {
      maxSizeMB = 3,
      maxWidthOrHeight = 720,
      quality = 0.7,
      maxRetries = 3,
    } = options;

    return new Observable((observer) => {
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB <= maxSizeMB) {
        observer.next(file);
        observer.complete();
        return;
      }

      this.compressVideoInBrowser(
        file,
        maxSizeMB,
        maxWidthOrHeight,
        quality,
      )
        .then((compressedFile) => {
          observer.next(compressedFile);
          observer.complete();
        })
        .catch((error) => {
          console.error('Video compression failed:', error);
          observer.next(file);
          observer.complete();
        });
    });
  }

  private async compressVideoInBrowser(
    file: File,
    maxSizeMB: number,
    maxWidthOrHeight: number,
    quality: number,
  ): Promise<File> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      if (!context) {
        reject(new Error('Canvas context not available'));
        return;
      }

      video.onloadedmetadata = async () => {
        try {
          const { width, height } = this.calculateDimensions(
            video.videoWidth,
            video.videoHeight,
            maxWidthOrHeight,
          );

          canvas.width = width;
          canvas.height = height;

          const stream = canvas.captureStream(15); // 15 fps for smaller file
          const mediaRecorder = new MediaRecorder(stream, {
            mimeType: 'video/webm;codecs=vp9',
            videoBitsPerSecond: this.calculateBitrate(
              maxSizeMB,
              video.duration,
            ),
          });

          const chunks: Blob[] = [];

          mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
              chunks.push(event.data);
            }
          };

          mediaRecorder.onstop = () => {
            const blob = new Blob(chunks, { type: 'video/webm' });
            const compressedFile = new File(
              [blob],
              file.name.replace(/\.[^/.]+$/, '.webm'),
              {
                type: 'video/webm',
                lastModified: Date.now(),
              },
            );

            URL.revokeObjectURL(video.src);
            video.remove();
            canvas.remove();

            resolve(compressedFile);
          };

          mediaRecorder.onerror = (error) => {
            reject(error);
          };

          mediaRecorder.start();

          video.play();
          const drawFrame = () => {
            if (!video.paused && !video.ended) {
              context.drawImage(video, 0, 0, width, height);
              requestAnimationFrame(drawFrame);
            } else {
              mediaRecorder.stop();
            }
          };
          drawFrame();
        } catch (error) {
          reject(error);
        }
      };

      video.onerror = () => {
        reject(new Error('Failed to load video'));
      };

      video.src = URL.createObjectURL(file);
      video.muted = true;
    });
  }

  private calculateDimensions(
    originalWidth: number,
    originalHeight: number,
    maxSize: number,
  ): { width: number; height: number } {
    if (originalWidth <= maxSize && originalHeight <= maxSize) {
      return { width: originalWidth, height: originalHeight };
    }

    const aspectRatio = originalWidth / originalHeight;
    let width: number;
    let height: number;

    if (originalWidth > originalHeight) {
      width = maxSize;
      height = Math.round(maxSize / aspectRatio);
    } else {
      height = maxSize;
      width = Math.round(maxSize * aspectRatio);
    }

    return { width, height };
  }

  private calculateBitrate(
    maxSizeMB: number,
    duration: number,
  ): number {
    const maxSizeBits = maxSizeMB * 8 * 1024 * 1024;
    const bitrate = Math.floor(maxSizeBits / duration);
    return Math.min(Math.max(bitrate, 100000), 2000000); // 100kbps to 2Mbps
  }

  isCompressionSupported(): boolean {
    return !!(
      window.MediaRecorder &&
      typeof document.createElement('canvas').captureStream ===
        'function' &&
      document.createElement('video').canPlayType('video/mp4')
    );
  }

  getRecommendedSettings(fileSizeMB: number): CompressionOptions {
    if (fileSizeMB > 50) {
      return {
        maxSizeMB: 2,
        maxWidthOrHeight: 480,
        quality: 0.5,
      };
    } else if (fileSizeMB > 20) {
      return {
        maxSizeMB: 3,
        maxWidthOrHeight: 720,
        quality: 0.6,
      };
    } else if (fileSizeMB > 10) {
      return {
        maxSizeMB: 5,
        maxWidthOrHeight: 720,
        quality: 0.7,
      };
    } else {
      return {
        maxSizeMB: 8,
        maxWidthOrHeight: 1080,
        quality: 0.8,
      };
    }
  }
}
